import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../PageHeader/PageHeader';
import { HitApi } from '../../../Api/ApiHit';
import { setPagination } from '../../../redux/Actions/TableAction';
import EditButton from '../../../components/Edit/EditButton';
import DeleteButton from '../../../components/Delete/DeleteButton';
import { setApiJson } from '../../../redux/Actions/ApiAction';
import { deleteBillboard, deleteMedia, searchMedia } from '../../../constant/Constant';
import AppTable from '../../../components/AppTable/AppTable';
import toast from 'react-hot-toast';
import { getSelectedUnit } from '../../../storage/Storage';
import { hasPermission } from '../../../utils/permissionUtils';
import TableSearch from '../../../components/TableSearch/TableSearch';

function BillBoardAds() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ApiReducer = useSelector((state) => state.ApiReducer);
  const TableDataReducer = useSelector((state) => state.TableReducer);

  // Search state variables
  const [optionSelect, setOptionSelect] = useState('');
  const [valueSearched, setValueSearched] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Calculate base index for serial numbers
  const baseIndex = (TableDataReducer.pagination.page - 1) * TableDataReducer.pagination.limit;

  // Permission checks using hasPermission utility
  const canEditBillboard = hasPermission('advertisement', 'write');
  const canReadBillboard = hasPermission('advertisement', 'read');
  const canDeleteBillboard = hasPermission('advertisement', 'delete');
  const canAddBillboard = hasPermission('advertisement', 'write');

  console.log({ canEditBillboard, canReadBillboard, canDeleteBillboard, canAddBillboard });

  useEffect(() => {
    // Only fetch billboards if user has read permission
    if (canReadBillboard) {
      getBillBoardData();
    }
  }, [TableDataReducer.pagination.page, TableDataReducer.pagination.limit, canReadBillboard]);

  const getBillBoardData = (searchKey, searchValue) => {
    if (!canReadBillboard) {
      toast.error('You do not have permission to view billboards');
      return;
    }

    setIsSearching(true);

    const searchObj = {
      unitIds: getSelectedUnit()?._id
    };

    // Only add search key/value if both are provided
    if (searchKey && searchValue) {
      searchObj[searchKey] = searchValue;
    }

    const json = {
      page: TableDataReducer.pagination.page,
      limit: TableDataReducer.pagination.limit,
      search: searchObj
    };

    HitApi(json, searchMedia).then((res) => {
      console.log("res", res);
      if (res?.statusCode === 200) {
        const billboardData = res?.data?.docs || (Array.isArray(res?.data) ? res?.data : []);
        dispatch(setApiJson(billboardData));

        // Update pagination data
        const paginationData = {
          total: res?.data?.totalDocs || res?.totalDocs || billboardData.length || 0,
          page: res?.data?.page || TableDataReducer.pagination.page,
          limit: res?.data?.limit || TableDataReducer.pagination.limit
        };

        dispatch(setPagination(paginationData));
      } else {
        dispatch(setApiJson([]));
        toast.error("Failed to load billboard data. Please try again.");
      }
    }).catch(error => {
      console.error("Error fetching billboards:", error);
      dispatch(setApiJson([]));
      toast.error("Error fetching billboards. Please try again.");
    }).finally(() => {
      setIsSearching(false);
    });
  };

  // Search functionality
  const handleSearch = () => {
    if (!canReadBillboard) {
      toast.error('You do not have permission to search billboards');
      return;
    }

    if (!optionSelect) {
      toast.error('Please select a search option');
      return;
    }
    if (!valueSearched || valueSearched.trim() === '') {
      toast.error('Please enter a search value');
      return;
    }
    getBillBoardData(optionSelect, valueSearched.trim());
  };

  // Clear search functionality
  const handleClearSearch = () => {
    if (!canReadBillboard) {
      toast.error('You do not have permission to access billboards');
      return;
    }

    setOptionSelect('');
    setValueSearched('');
    getBillBoardData();
  };

  // Search options based on table keys
  const options = [
    { label: 'Type', value: 'type' },
    { label: 'Duration', value: 'duration' },
    { label: 'Status', value: 'isActive' },
  ];

  const handleEdit = (row) => {
    if (!canEditBillboard) {
      toast.error('You do not have permission to edit billboards');
      return;
    }
    navigate(`/billboard/editbillboard/${row._id}`);
  };

  const handleDeleteBillboard = (row) => {
    if (!canDeleteBillboard) {
      toast.error('You do not have permission to delete billboards');
      return;
    }

    if (window.confirm(`Are you sure you want to delete this billboard?`)) {
      const json = {
        _id: row._id
      };

      console.log("json", json);

      HitApi(json, deleteMedia).then((res) => {
        console.log('res', res);
        if (res?.success) {
          toast.success("Billboard deleted successfully!");
          getBillBoardData();
        } else {
          toast.error("Failed to delete billboard. Please try again.");
        }
      }).catch(error => {
        console.error("Error deleting billboard:", error);
        toast.error("Error occurred while deleting billboard. Please try again.");
      });
    }
  };

  const renderStatus = (status) => {
    if (status === undefined || status === null) return null;

    let bgColor = "bg-gray-100";
    let textColor = "text-gray-800";

    switch (status.toLowerCase()) {
      case "active":
        bgColor = "bg-green-100";
        textColor = "text-green-800";
        break;
      case "inactive":
        bgColor = "bg-red-100";
        textColor = "text-red-800";
        break;
      case "pending":
        bgColor = "bg-yellow-100";
        textColor = "text-yellow-800";
        break;
      default:
        bgColor = "bg-gray-100";
        textColor = "text-gray-800";
        break;
    }

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {status}
      </span>
    );
  };

  // Format the duration in a readable format
  const formatDuration = (duration) => {
    if (!duration) return 'N/A';

    const durationNumber = parseInt(duration, 10);
    if (isNaN(durationNumber)) return 'N/A';

    // Duration is already in seconds from API
    return `${durationNumber}s`;
  };

  // Define base table headers
  const baseTableHeaders = [
    {
      title: "S.No",
      key: "serialNumber",
      width: "60px",
      align: "center",
      render: (_, row, index) => {
        // Calculate serial number based on current page and limit
        const serialNumber = baseIndex + index + 1;
        return (
          <div className="text-center">
            <span className="text-sm font-medium text-gray-900">{serialNumber}</span>
          </div>
        );
      }
    },
    {
      title: "Media",
      key: "source",
      render: (value, row) => {
        if (!value) return (
          <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
            <span className="text-xs text-gray-500">No Media</span>
          </div>
        );

        if (row.type === 'image' || row.type === 'gif') {
          return (
            <img
              src={value}
              alt="Billboard"
              className="w-16 h-16 object-cover rounded"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/placeholder-image.png'; // Fallback image
              }}
            />
          );
        } else if (row.type === 'video') {
          return (
            <div className="w-16 h-16 bg-black rounded flex items-center justify-center text-white text-xs">
              <span>Video</span>
            </div>
          );
        }

        return (
          <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
            <span className="text-xs text-gray-500">Unknown</span>
          </div>
        );
      }
    },
    {
      title: "Device",
      key: "deviceId",
      render: (value, row) => {
        if (!value || !row.deviceId) return (
          <span className="text-sm text-gray-500">N/A</span>
        );

        const device = row.deviceId;
        return (
          <div>
            <div className="font-medium text-gray-900 text-sm">{device.deviceCode || 'N/A'}</div>
            <div className="text-xs text-gray-500">{device.deviceName || ''}</div>
            {device.deviceType && (
              <div className="text-xs text-gray-400">{device.deviceType}</div>
            )}
          </div>
        );
      }
    },
    {
      title: "Type",
      key: "type",
      render: (value) => {
        if (!value) return (
          <span className="text-sm text-gray-500">N/A</span>
        );

        // Capitalize first letter and add styling
        const formattedType = value.charAt(0).toUpperCase() + value.slice(1);
        const typeColors = {
          'Image': 'bg-blue-100 text-blue-800',
          'Video': 'bg-purple-100 text-purple-800',
          'Gif': 'bg-green-100 text-green-800'
        };

        const colorClass = typeColors[formattedType] || 'bg-gray-100 text-gray-800';

        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
            {formattedType}
          </span>
        );
      }
    },
    {
      title: "Duration",
      key: "duration",
      render: (value) => (
        <span className="px-2 py-1 bg-orange-100 rounded text-sm font-medium text-orange-800">
          {formatDuration(value)}
        </span>
      )
    },
    {
      title: "Unique View Count",
      key: "uniqueViewCount",
      align: "center",
      render: (value) => (
        <div className="text-center">
          <span className="px-3 py-1 bg-indigo-100 rounded-full text-sm font-semibold text-indigo-800">
            {value || 0}
          </span>
        </div>
      )
    },
    {
      title: "Status",
      key: "isActive",
      render: (value) => {
        // Use the actual boolean value directly
        const status = value === true ? "active" : "inactive";
        return renderStatus(status);
      }
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (value, row) => {
        // Check if user has any action permissions
        const hasAnyPermission = canEditBillboard || canDeleteBillboard;

        if (!hasAnyPermission) {
          return (
            <div className="flex justify-center">
              <span className="text-sm text-gray-400">No actions available</span>
            </div>
          );
        }

        return (
          <div className="flex justify-center space-x-2">
            {canEditBillboard && (
              <EditButton
                onEdit={() => handleEdit(row)}
                itemName="billboard"
                tooltip="Edit Billboard"
              />
            )}
            {canDeleteBillboard && (
              <DeleteButton
                onDelete={() => handleDeleteBillboard(row)}
                itemName="billboard"
                tooltip="Delete Billboard"
              />
            )}
          </div>
        );
      }
    }
  ];

  // Only show actions column if user has any action permissions
  const shouldShowActionsColumn = canEditBillboard || canDeleteBillboard;
  const tableHeaders = shouldShowActionsColumn
    ? baseTableHeaders
    : baseTableHeaders.filter(header => header.key !== 'actions');

  // Handle row click if needed
  const handleRowClick = (row) => {
    if (!canReadBillboard) {
      toast.error('You do not have permission to view billboard details');
      return;
    }
    console.log("Billboard details:", row);
    // Show billboard details or open a modal
  };

  // Show access denied message if user doesn't have read permission
  if (!canReadBillboard) {
    return (
      <div className="p-5">
        <PageHeader
          title={'Billboard Advertisements'}
          description={'Create and manage billboard advertisements'}
        />
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <h3 className="text-lg font-medium text-red-600 mb-2">Access Denied</h3>
            <p className="text-gray-600">You don't have permission to view billboards.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5">
      <PageHeader
        title={'Billboard Advertisements'}
        description={'Create and manage billboard advertisements'}
      />
      <AppTable
        TH={tableHeaders}
        TD={Array.isArray(ApiReducer.apiJson) ? ApiReducer.apiJson : []}
        onRowClick={handleRowClick}
        isLoading={!Array.isArray(ApiReducer.apiJson) || ApiReducer.apiJson.length === 0 || isSearching}
        emptyMessage="No billboards found"
        buttonText={canAddBillboard ? "New Billboard" : undefined}
        navigateTo={canAddBillboard ? "/addbillboard" : undefined}
        TableSearch={
          <TableSearch
            options={options}
            onClick={handleSearch}
            optionSelect={optionSelect}
            setOptionSelect={setOptionSelect}
            setValueSearched={setValueSearched}
            valueSearched={valueSearched}
            onClear={handleClearSearch}
          />
        }
      />
    </div>
  );
}

export default BillBoardAds;